/* =====================================================================
 * useActiveHeading — 스크롤 위치로 "지금 읽고 있는 절" 을 찾는다.
 *
 * 본문 오른쪽 목차와 왼쪽 목차의 절 목록이 같은 값을 보도록 페이지에서 한 번만
 * 계산해 내려준다. 제목이 컨테이너 위쪽에서 ACTIVE_OFFSET 안으로 올라오면 그
 * 절을 읽는 중으로 보고, 바닥에 닿으면 마지막 절로 본다(마지막 절이 짧으면
 * 제목이 기준선까지 못 올라오기 때문).
 * ===================================================================== */

import { useEffect, useState } from 'react';

import type { GuideHeading } from './guideOutline';

const ACTIVE_OFFSET = 96;

export function useActiveHeading(
  headings: readonly GuideHeading[],
  scrollRootRef: React.RefObject<HTMLElement>,
): string | null {
  const [activeId, setActiveId] = useState<string | null>(headings[0]?.id ?? null);

  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root || headings.length === 0) {
      setActiveId(null);
      return undefined;
    }

    let frame = 0;
    const update = () => {
      frame = 0;
      const rootTop = root.getBoundingClientRect().top;
      let current = headings[0].id;
      for (const heading of headings) {
        const element = root.querySelector<HTMLElement>(`#${heading.id}`);
        if (!element) continue;
        if (element.getBoundingClientRect().top - rootTop <= ACTIVE_OFFSET) current = heading.id;
      }
      if (root.scrollTop > 0 && root.scrollTop + root.clientHeight >= root.scrollHeight - 4) {
        current = headings[headings.length - 1].id;
      }
      setActiveId(current);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      root.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [headings, scrollRootRef]);

  return activeId;
}

/** 절 제목 요소를 찾는다.
 *  예전 주소의 `section-N` 은 N 번째 절로 찾아 준다(이름 붙은 앵커 도입 전 링크 호환). */
function findGuideHeading(root: HTMLElement, id: string): HTMLElement | undefined {
  const target = Array.from(root.querySelectorAll<HTMLElement>('[id]')).find((el) => el.id === id);
  if (target) return target;
  const legacy = /^section-(\d+)$/.exec(id);
  return legacy ? root.querySelectorAll<HTMLElement>('article h2')[Number(legacy[1]) - 1] : undefined;
}

/** 절 제목이 컨테이너 맨 위에 오려면 필요한 scrollTop. 끝까지 내려가도 못 올라오는
 *  마지막 절은 최대 스크롤 위치로 자른다. 찾지 못하면 null. */
function headingScrollTop(root: HTMLElement, id: string): number | null {
  const target = findGuideHeading(root, id);
  if (!target) return null;
  const margin = Number.parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
  const top = root.scrollTop + target.getBoundingClientRect().top - root.getBoundingClientRect().top - margin;
  const max = root.scrollHeight - root.clientHeight;
  return Math.max(0, max > 0 ? Math.min(top, max) : top);
}

/** 절 제목으로 바로 이동한다(애니메이션 없음). */
export function scrollToGuideHeading(root: HTMLElement | null, id: string): void {
  if (!root) return;
  const top = headingScrollTop(root, id);
  if (top !== null) root.scrollTop = top;
}

/* 스크롤 컨테이너마다 진행 중인 절 이동은 하나뿐이다. 새로 이동하면 앞의 것을
 * 멈춘다 — 둘이 동시에 돌면 서로 다른 절로 번갈아 끌어당긴다. */
const activeSettles = new WeakMap<HTMLElement, () => void>();

/** 진행 중인 절 이동을 멈춘다(맨 위로 이동 등, 다른 곳으로 스크롤할 때). */
export function cancelGuideSettle(root: HTMLElement | null): void {
  if (root) activeSettles.get(root)?.();
}

/* 살며시 출발해 부드럽게 멈춘다. 가운데 속도가 기본 거리의 1.57배로, 3차 곡선(3배)보다
 * 완만해서 먼 절로 이어서 움직여도 급하게 튀지 않는다. */
const easeInOutSine = (t: number) => -(Math.cos(Math.PI * t) - 1) / 2;

/** 거리에 따른 이동 시간. 가까우면 짧게, 멀수록 조금씩 길게, 1.3초를 넘기지 않는다. */
export function guideGlideDuration(distance: number): number {
  if (distance <= 1000) return 420 + distance * 0.36;
  return Math.min(1300, 780 + (distance - 1000) / 25);
}

/* 목표가 움직일 때(캡처 로드) 따라가는 비율. 한 프레임에 차이의 이만큼만 좇아간다. */
const TARGET_FOLLOW = 0.18;

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface GuideHeadingNavigation {
  /** 스르륵 움직일지. 공유 주소로 처음 들어올 때처럼 이미 목적지를 기대하는 경우는 false. */
  readonly smooth?: boolean;
  /** 도착한 뒤에도 늦게 로드되는 캡처에 맞춰 위치를 다시 맞추는 시간. */
  readonly timeoutMs?: number;
}

/** 목차 · 문서 안 링크 · 공유 주소로 특정 절에 간다.
 *
 *  캡처와 영상은 화면 가까이 와야 로드되며 높이가 생긴다. 브라우저 기본 smooth 스크롤은
 *  출발할 때 정한 위치로만 가서, 이동 중에 위쪽 캡처가 커지면 도중에 멈추거나 목표 앞에
 *  선다(실사고: 목차에서 "자주 묻는 질문" 을 눌러도 이동 안 함). 그래서 직접 애니메이션하며
 *  **매 프레임 목표 절의 현재 위치를 다시 읽어** 따라가고, 도착한 뒤에는 본문 높이가 바뀔
 *  때마다(이미지 · 영상 로드 포함) 다시 맞춘다. 사용자가 직접 스크롤을 시작하면
 *  (휠 · 터치 · 키 · 마우스) 그 자리에서 멈춘다. */
export function settleOnGuideHeading(
  root: HTMLElement | null,
  id: string,
  { smooth = true, timeoutMs = 3000 }: GuideHeadingNavigation = {},
): () => void {
  if (!root) return () => undefined;
  cancelGuideSettle(root);

  let frame = 0;
  let timer = 0;
  let stopped = false;
  let restoreAnchor = () => {};
  let observer: ResizeObserver | null = null;
  let media: Element[] = [];
  const realign = () => scrollToGuideHeading(root, id);

  const follow = () => {
    realign();
    const content = root.firstElementChild ?? root;
    observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(realign);
    observer?.observe(content);
    media = Array.from(root.querySelectorAll('img, video'));
    media.forEach((el) => {
      el.addEventListener('load', realign);
      el.addEventListener('loadedmetadata', realign);
    });
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    window.cancelAnimationFrame(frame);
    restoreAnchor();
    observer?.disconnect();
    media.forEach((el) => {
      el.removeEventListener('load', realign);
      el.removeEventListener('loadedmetadata', realign);
    });
    root.removeEventListener('wheel', stop);
    root.removeEventListener('touchstart', stop);
    root.removeEventListener('keydown', stop);
    root.removeEventListener('mousedown', stop);
    window.clearTimeout(timer);
    if (activeSettles.get(root) === stop) activeSettles.delete(root);
  };
  root.addEventListener('wheel', stop, { passive: true });
  root.addEventListener('touchstart', stop, { passive: true });
  root.addEventListener('keydown', stop);
  root.addEventListener('mousedown', stop);
  activeSettles.set(root, stop);

  const firstTarget = headingScrollTop(root, id);
  const from = root.scrollTop;
  const distance = firstTarget === null ? 0 : Math.abs(firstTarget - from);

  if (firstTarget === null || !smooth || distance < 2 || prefersReducedMotion()) {
    follow();
    timer = window.setTimeout(stop, timeoutMs);
    return stop;
  }

  /* 지금 위치에서 목표까지 순간이동 없이 한 번에 움직인다.
   * 가는 도중 캡처가 로드되어 목표가 밀리면 출발을 다시 하지 않고(속도가 끊겨 멈칫한다)
   * `aim` 을 실제 목표 쪽으로 매 프레임 조금씩 옮겨 흐름을 유지한 채 따라간다.
   * 브라우저의 스크롤 고정(overflow-anchor)이 사이사이 위치를 바꾸지 않게 잠시 끈다. */
  const duration = guideGlideDuration(distance);
  const anchorBefore = root.style.overflowAnchor;
  root.style.overflowAnchor = 'none';
  restoreAnchor = () => { root.style.overflowAnchor = anchorBefore; };
  let aim = firstTarget;
  const startedAt = performance.now();
  const step = (now: number) => {
    if (stopped) return;
    const target = headingScrollTop(root, id);
    if (target === null) {
      stop();
      return;
    }
    aim += (target - aim) * TARGET_FOLLOW;
    const progress = Math.min(1, Math.max(0, now - startedAt) / duration);
    const position = from + (aim - from) * easeInOutSine(progress);
    root.scrollTop = position;
    /* 시간이 다 됐어도 밀린 목표를 아직 다 좇지 못했으면 몇 프레임 더 부드럽게 붙는다. */
    if (progress < 1 || Math.abs(target - aim) > 1) {
      frame = window.requestAnimationFrame(step);
      return;
    }
    root.scrollTop = target;
    restoreAnchor();
    follow();
    timer = window.setTimeout(stop, timeoutMs);
  };
  frame = window.requestAnimationFrame(step);
  return stop;
}
